/**
 * O que faz: define densidade e hierarquia dos cards das configurações.
 * Onde usa: formulários, listas e subpainéis das abas de Configurações.
 * Conexões: componentes Card compartilhados e utilitário cn.
 * Tabelas: nenhuma.
 * Data: 30/08/2026 (raio de card do Gens Design System em 07/10/2026).
 */
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

type CartaoProps = ComponentPropsWithoutRef<typeof Card> & {
  nivel?: 'principal' | 'interno';
};
type ConteudoProps = ComponentPropsWithoutRef<typeof CardContent> & {
  semCabecalho?: boolean;
};

// A base mantém tema e raio; grupos internos dispensam outra camada de sombra.
export const CartaoConfiguracoes = forwardRef<HTMLDivElement, CartaoProps>(
  ({ nivel = 'principal', className, ...props }, ref) => (
    <Card
      ref={ref}
      // Raio de card do design system (24 px); grupos internos dispensam outra camada de sombra.
      className={cn('min-w-0 rounded-3xl', nivel === 'interno' && 'rounded-2xl shadow-none', className)}
      {...props}
    />
  ),
);
CartaoConfiguracoes.displayName = 'CartaoConfiguracoes';

export const CabecalhoCartaoConfiguracoes = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CardHeader>>(
  ({ className, ...props }, ref) => <CardHeader ref={ref} className={cn('p-4 sm:p-5', className)} {...props} />,
);
CabecalhoCartaoConfiguracoes.displayName = 'CabecalhoCartaoConfiguracoes';

export const TituloCartaoConfiguracoes = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<typeof CardTitle>>(
  ({ className, ...props }, ref) => <CardTitle ref={ref} className={cn('text-base leading-snug', className)} {...props} />,
);
TituloCartaoConfiguracoes.displayName = 'TituloCartaoConfiguracoes';

export const DescricaoCartaoConfiguracoes = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<typeof CardDescription>>(
  ({ className, ...props }, ref) => <CardDescription ref={ref} className={cn('leading-relaxed text-muted-foreground', className)} {...props} />,
);
DescricaoCartaoConfiguracoes.displayName = 'DescricaoCartaoConfiguracoes';

// Sem header interno, o conteúdo precisa do padding superior completo.
export const ConteudoCartaoConfiguracoes = forwardRef<HTMLDivElement, ConteudoProps>(
  ({ semCabecalho = false, className, ...props }, ref) => (
    <CardContent
      ref={ref}
      className={cn('p-4 sm:p-5', !semCabecalho && 'pt-0 sm:pt-0', className)}
      {...props}
    />
  ),
);
ConteudoCartaoConfiguracoes.displayName = 'ConteudoCartaoConfiguracoes';

export const RodapeCartaoConfiguracoes = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CardFooter>>(
  ({ className, ...props }, ref) => <CardFooter ref={ref} className={cn('flex-wrap gap-2 p-4 pt-0 sm:p-5 sm:pt-0', className)} {...props} />,
);
RodapeCartaoConfiguracoes.displayName = 'RodapeCartaoConfiguracoes';
